import React, { useState, useMemo, useEffect } from 'react';
import { FirebaseService } from '../../services/firebaseService';

// --- ONTOLOGIA CLINICA INTEGRATA (Definizioni rigorose) ---

const MODELS = {
    AT: {
        title: "Analisi Transazionale (Mente/Copione)",
        color: "indigo",
        icon: "✨",
        markers: [
            { id: 'gc', label: "Genitore Critico", state: 'G', keywords: ['deve', 'sbagliato', 'colpa', 'assurdo', 'sempre', 'mai'] },
            { id: 'ga', label: "Genitore Affettivo", state: 'G', keywords: ['aiuto', 'cura', 'proteggo', 'bravo', 'tranquillo'] },
            { id: 'a', label: "Adulto (Decontaminato)", state: 'A', keywords: ['logica', 'analisi', 'dati', 'perché', 'come', 'fatto'] },
            { id: 'bl', label: "Bambino Libero", state: 'B', keywords: ['voglio', 'gioco', 'bello', 'sorpresa', 'energia'] },
            { id: 'ba', label: "Bambino Adattato", state: 'B', keywords: ['scusa', 'permesso', 'paura', 'provo', 'forse'] },
            { id: 'perfetto', label: "Spinta: Sii Perfetto", state: 'G', keywords: ['errore', 'perfetto', 'esatto', 'giusto', 'ordine'] },
            { id: 'forte', label: "Spinta: Sii Forte", state: 'A', keywords: ['forza', 'resisto', 'nulla', 'nascondo', 'solo'] }
        ]
    },
    BIO: {
        title: "Bioenergetica (Corpo/Energia)",
        color: "emerald",
        icon: "🧬",
        markers: [
            { id: 'oculare', label: "Blocco Oculare (Contatto visivo)", state: 'B', keywords: ['occhi', 'sguardo', 'vista', 'vuoto', 'perso'] },
            { id: 'orale', label: "Blocco Orale (Mascella/Rabbia)", state: 'B', keywords: ['mascella', 'serro', 'denti', 'gridare', 'mordo'] },
            { id: 'toracico', label: "Blocco Toracico (Affettività)", state: 'G', keywords: ['petto', 'spalle', 'respiro', 'stretto', 'fiato'] },
            { id: 'diaframma', label: "Blocco Diaframmatico (Tenuta)", state: 'A', keywords: ['nausea', 'stomaco', 'taglio', 'blocco'] },
            { id: 'pelvico', label: "Blocco Pelvico (Vitalità)", state: 'B', keywords: ['bacino', 'pelvi', 'sesso', 'movimento'] }
        ]
    },
    GESTALT: {
        title: "Gestalt (Relazione/Contatto)",
        color: "purple",
        icon: "🎭",
        markers: [
            { id: 'proiezione', label: "Proiezione (Attribuzione)", state: 'B', keywords: ['lui pensa', 'loro dicono', 'giudicano', 'specchio'] },
            { id: 'retroflessione', label: "Retroflessione (Trattenimento)", state: 'B', keywords: ['me stesso', 'trattengo', 'dentro', 'colpa mia'] },
            { id: 'deflessione', label: "Deflessione (Evitamento)", state: 'A', keywords: ['battuta', 'ridere', 'ironia', 'cambio discorso'] },
            { id: 'confluenza', label: "Confluenza (Fusione)", state: 'B', keywords: ['noi', 'uguali', 'pace', 'fusione'] }
        ]
    }
};

const SupervisionHub = ({ clients = [], onBack }) => {
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedMarkers, setSelectedMarkers] = useState({});
    const [evidence, setEvidence] = useState([]);
    const [loading, setLoading] = useState(false);

    // --- 1. INTELLIGENT SCANNER (SOLO DATI REALI) ---
    useEffect(() => {
        const analyzeStory = async () => {
            if (!selectedClient) return;
            setLoading(true);
            try {
                const [meetings, works] = await Promise.all([
                    FirebaseService.getMeetingsByClient(selectedClient.id),
                    FirebaseService.getWorksByClient(selectedClient.id, true)
                ]);

                const corpus = [...meetings, ...works.filter(w => w.shared)];
                const detectedEvidence = [];

                // Scansione per modelli
                Object.entries(MODELS).forEach(([modelKey, model]) => {
                    model.markers.forEach(marker => {
                        corpus.forEach(item => {
                            const text = (typeof item.content === 'string' ? item.content : item.notes || item.content?.text || "").toLowerCase();
                            const found = marker.keywords.find(kw => text.includes(kw));

                            if (found && !detectedEvidence.find(e => e.markerId === marker.id)) {
                                detectedEvidence.push({
                                    markerId: marker.id,
                                    label: marker.label,
                                    modelIcon: model.icon,
                                    modelColor: model.color,
                                    quote: text.substring(text.indexOf(found) - 40, text.indexOf(found) + 60).trim() + "...",
                                    keyword: found,
                                    timestamp: item.date || item.timestamp
                                });
                            }
                        });
                    });
                });

                // Ordina evidenze per data
                setEvidence(detectedEvidence.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
            } catch (err) {
                console.error("Analysis Error:", err);
            } finally {
                setLoading(false);
            }
        };
        analyzeStory();
    }, [selectedClient]);

    const toggleMarker = (id) => {
        setSelectedMarkers(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // --- 2. SINTESI A SPIRALE + TIMELINE (Logica .md) ---
    const synthesis = useMemo(() => {
        const activeIds = Object.keys(selectedMarkers).filter(id => selectedMarkers[id]);
        if (activeIds.length === 0) return null;

        const activeMarkers = [];
        const energyGroups = { G: 10, A: 10, B: 10 };

        Object.entries(MODELS).forEach(([mKey, model]) => {
            model.markers.forEach(m => {
                if (activeIds.includes(m.id)) {
                    activeMarkers.push({ ...m, modelIcon: model.icon, modelColor: model.color });
                    energyGroups[m.state] += 25;
                }
            });
        });

        // Calcolo Egogramma
        const total = energyGroups.G + energyGroups.A + energyGroups.B;
        const egogram = { G: (energyGroups.G / total) * 100, A: (energyGroups.A / total) * 100, B: (energyGroups.B / total) * 100 };

        // Generazione Timeline Fluida (Basata su evidenze confermate)
        const confirmedEvidence = evidence.filter(e => activeIds.includes(e.markerId))
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

        const timeline = confirmedEvidence.map((ev, idx) => {
            let prefix = idx === 0 ? "Inizialmente emerge " :
                idx === confirmedEvidence.length - 1 ? "Più recentemente si osserva " :
                    "Nel prosieguo si evidenzia ";
            return `${prefix}**${ev.label}** (_"${ev.quote}"_)`;
        });

        return { egogram, activeMarkers, timeline };
    }, [selectedMarkers, evidence]);

    return (
        <div className="h-screen bg-slate-50 flex flex-col font-sans overflow-hidden">
            {/* Minimal Header */}
            <header className="px-10 py-6 bg-white border-b border-gray-100 flex justify-between items-center shrink-0">
                <div className="flex items-center gap-6">
                    <button onClick={onBack} className="h-10 w-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-400 hover:text-indigo-600 transition-colors">←</button>
                    <div>
                        <h1 className="text-2xl font-black text-slate-800 tracking-tight">Supervisore Senior Integrato</h1>
                        <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Metodo Olistico: AT • BIO • GESTALT</p>
                    </div>
                </div>
                {selectedClient && (
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-black text-slate-400 uppercase">Caso: {selectedClient.name}</span>
                        <div className="h-8 w-px bg-slate-100"></div>
                        <button onClick={() => setSelectedMarkers({})} className="text-[10px] font-bold text-slate-400 hover:text-red-500 transition-colors">RESET ANALISI</button>
                    </div>
                )}
            </header>

            <main className="flex-1 flex overflow-hidden">
                {!selectedClient ? (
                    <div className="flex-1 flex items-center justify-center p-20">
                        <div className="max-w-xl w-full text-center">
                            <h2 className="text-xl font-black text-slate-400 uppercase tracking-widest mb-10">Seleziona il cliente da supervisionare</h2>
                            <div className="grid grid-cols-2 gap-4">
                                {clients.map(c => (
                                    <button key={c.id} onClick={() => setSelectedClient(c)} className="p-8 bg-white border border-gray-100 rounded-[2rem] shadow-sm hover:shadow-xl hover:border-indigo-500 transition-all font-black text-slate-700">{c.name}</button>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* AREA 1: EVIDENZE & MATRICE */}
                        <div className="flex-1 overflow-y-auto p-12 space-y-16">

                            {/* Citazioni Reali (Evidence Feed) */}
                            <section>
                                <div className="flex items-center gap-4 mb-8">
                                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Evidenze estratte dal percorso</h3>
                                    <div className="h-px flex-1 bg-slate-100"></div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {loading ? <div className="p-10 text-center text-slate-400 animate-pulse font-bold">Rilevamento frammenti significativi...</div> : (
                                        evidence.length > 0 ? evidence.map((ev, i) => (
                                            <div key={i} className={`p-6 bg-white rounded-3xl border-2 transition-all relative overflow-hidden flex flex-col justify-between ${selectedMarkers[ev.markerId] ? `border-${ev.modelColor}-200 shadow-md` : 'border-slate-50 hover:border-indigo-100'}`}>
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-2">
                                                        <span>{ev.modelIcon}</span>
                                                        <span className={`text-[10px] font-black uppercase text-${ev.modelColor}-600 tracking-tighter`}>{ev.label}</span>
                                                    </div>
                                                    <button
                                                        onClick={() => toggleMarker(ev.markerId)}
                                                        className={`text-[9px] font-black uppercase px-2 py-1 rounded-md transition-all ${selectedMarkers[ev.markerId] ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}
                                                    >
                                                        {selectedMarkers[ev.markerId] ? 'Nell\'Analisi ✓' : 'Analizza +'}
                                                    </button>
                                                </div>
                                                <p className="text-xs text-slate-500 leading-relaxed italic">"...{ev.quote}"</p>
                                                <div className="mt-3 text-[8px] font-bold text-slate-300 uppercase">{new Date(ev.timestamp).toLocaleDateString('it-IT')}</div>
                                            </div>
                                        )) : <p className="text-xs text-slate-400 italic">Nessun marcatore rilevato automaticamente nei testi.</p>
                                    )}
                                </div>
                            </section>

                            {/* Matrice Manuale */}
                            <section className="space-y-12 pb-20">
                                {Object.entries(MODELS).map(([key, model]) => (
                                    <div key={key} className="space-y-6">
                                        <div className="flex items-center gap-4">
                                            <h4 className={`text-xs font-black uppercase tracking-[0.2em] text-${model.color}-600`}>{model.title}</h4>
                                            <div className="h-px flex-1 bg-slate-100"></div>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {model.markers.map(m => (
                                                <button
                                                    key={m.id}
                                                    onClick={() => toggleMarker(m.id)}
                                                    className={`px-5 py-3 rounded-2xl text-xs font-bold transition-all border-2 ${selectedMarkers[m.id]
                                                        ? `bg-${model.color}-600 text-white border-${model.color}-600 shadow-lg scale-105`
                                                        : 'bg-white text-slate-400 border-slate-100 hover:border-slate-300'}`}
                                                >
                                                    {m.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </section>
                        </div>

                        {/* AREA 2: SINTESI & REPORT (SIDEBAR) */}
                        <aside className="w-[480px] bg-white border-l border-gray-100 flex flex-col shrink-0 overflow-y-auto">
                            {!synthesis ? (
                                <div className="flex-1 flex flex-col items-center justify-center p-20 text-center opacity-20">
                                    <span className="text-7xl mb-6">👁️</span>
                                    <p className="text-xs font-black uppercase tracking-widest">In attesa di dati confermati</p>
                                </div>
                            ) : (
                                <div className="p-10 space-y-16 animate-fade-in">
                                    {/* Mappa Energetica */}
                                    <section>
                                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-10 text-center">Evoluzione Stati GAB</h3>
                                        <div className="flex items-end justify-around h-40 gap-8 px-8">
                                            {['G', 'A', 'B'].map(state => (
                                                <div key={state} className="flex-1 flex flex-col items-center">
                                                    <div className="w-full bg-slate-50 rounded-full h-full relative overflow-hidden flex items-end border border-slate-100">
                                                        <div className={`w-full transition-all duration-1000 ${state === 'G' ? 'bg-rose-500' : state === 'A' ? 'bg-emerald-500' : 'bg-indigo-600'}`} style={{ height: `${synthesis.egogram[state]}%` }} />
                                                    </div>
                                                    <span className="mt-4 text-[10px] font-black text-slate-500 uppercase">{state === 'G' ? 'Genitore' : state === 'A' ? 'Adulto' : 'Bambino'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </section>

                                    {/* Sintesi Narrativa a Timeline (Logica .md) */}
                                    <section className="space-y-8">
                                        <div className="flex items-center gap-3">
                                            <span className="text-xl">📈</span>
                                            <h3 className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Timeline del Percorso</h3>
                                        </div>
                                        <div className="space-y-6">
                                            {synthesis.timeline.length > 0 ? (
                                                <div className="p-8 rounded-[2rem] bg-indigo-50/50 border border-indigo-100">
                                                    <div className="text-xs text-indigo-900 leading-relaxed font-bold space-y-2">
                                                        {synthesis.timeline.map((text, idx) => (
                                                            <p key={idx}>{text}.</p>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-[10px] text-slate-400 italic">Conferma i marcatori con le quote per generare la timeline.</p>
                                            )}
                                        </div>
                                    </section>

                                    {/* Lista Marcatori Attivi */}
                                    <section className="pt-10 border-t border-slate-100">
                                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Sintesi dei Marcatori</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {synthesis.activeMarkers.map(m => (
                                                <div key={m.id} className={`px-3 py-1.5 rounded-lg border border-${m.modelColor}-100 bg-${m.modelColor}-50/30 text-[9px] font-black text-${m.modelColor}-600 uppercase`}>
                                                    {m.modelIcon} {m.label}
                                                </div>
                                            ))}
                                        </div>
                                    </section>

                                    <button
                                        onClick={async () => {
                                            const result = {
                                                clientId: selectedClient.id,
                                                date: new Date().toISOString(),
                                                markers: synthesis.activeMarkers.map(m => m.id),
                                                timeline: synthesis.timeline
                                            };
                                            await FirebaseService.saveAtLabSession(selectedClient.id, result);
                                            alert("✨ Rapporto di supervisione clinica archiviato.");
                                        }}
                                        className="w-full py-6 bg-indigo-600 text-white rounded-[2rem] text-sm font-black shadow-xl hover:bg-slate-900 active:scale-95 transition-all uppercase tracking-[0.2em]"
                                    >
                                        Salva Analisi Senior
                                    </button>
                                </div>
                            )}
                        </aside>
                    </>
                )}
            </main>

            <style dangerouslySetInnerHTML={{
                __html: `
                .animate-fade-in { animation: fadeIn 0.4s ease-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}} />
        </div>
    );
};

export default SupervisionHub;
